import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { leadService, LeadFilters } from '../services/leadService';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { Lead, CallLog, CallStatistics } from '../types';
import { useAppData } from '../context/AppDataContext';
import { FiPhone, FiCalendar, FiClock, FiUser, FiMessageSquare, FiCheckCircle, FiXCircle, FiArrowLeft, FiUserPlus, FiX, FiMail } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import PriceUpdateHistory from '../components/price/PriceUpdateHistory';
import ClickToCallButton from '../components/calls/ClickToCallButton';
import { formatDateTime, getTodayISO, getNowTimeHHMM } from '../utils/dateUtils';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import { getSettingColorClass } from '../utils/settingColors';

type LeadNavState = {
  assignmentId?: number;
  brandId?: number;
  listRestore?: { page: number; perPage: number; filters: LeadFilters };
};

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const { brands, refreshBrands } = useAppData();
  const [lead, setLead] = useState<Lead | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [statistics, setStatistics] = useState<CallStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertFormData, setConvertFormData] = useState({
    brand_id: undefined as number | undefined,
    status: 'inactive' as string,
    customer_code: '',
    sap_id: '',
    work_order_value: undefined as number | undefined,
    notes: ''
  });
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'lead',
    entity_id: parseInt(id || '0'),
    lead_assignment_id: undefined,
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound'
  });

  // Refresh brands and load lead on page visit
  useEffect(() => {
    if (id) {
      const loadData = async () => {
        await refreshBrands();
        await loadLead();
        loadStatistics();
      };
      loadData();
    }
  }, [id]);

  // Load call logs user-wise when lead or selected assignment changes
  useEffect(() => {
    if (!id || !lead) return;
    const assignments = lead.assignments || [];
    const sel = selectedAssignmentId
      ? assignments.find((a: { id: number }) => a.id === selectedAssignmentId)
      : assignments[0];
    loadCallLogs(sel?.user_id);
  }, [id, lead?.id, selectedAssignmentId]);

  const loadLead = async () => {
    try {
      setLoading(true);
      const data = await leadService.getById(parseInt(id || '0'));
      setLead(data);
      const stateAssignmentId = (location.state as LeadNavState | null)?.assignmentId;
      const assignments = data.assignments || [];
      if (stateAssignmentId && assignments.some((a: { id: number }) => a.id === stateAssignmentId)) {
        setSelectedAssignmentId(stateAssignmentId);
      } else if (assignments.length > 0) {
        setSelectedAssignmentId(assignments[0].id);
      } else {
        setSelectedAssignmentId(null);
      }
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async (calledByUserId?: number) => {
    try {
      const params = calledByUserId != null ? { called_by: calledByUserId } : undefined;
      const data = await callLogService.getByEntity('lead', parseInt(id || '0'), params);
      setCallLogs(data);
    } catch (err) {
      console.error('Failed to load call logs', err);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await callLogService.getStatistics('lead', parseInt(id || '0'));
      setStatistics(data);
    } catch (err) {
      console.error('Failed to load statistics', err);
    }
  };

  const handleCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await callLogService.create(callFormData);
      setShowCallModal(false);
      setCallFormData({
        entity_type: 'lead',
        entity_id: parseInt(id || '0'),
        lead_assignment_id: selectedAssignmentId ?? undefined,
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound'
      });
      loadLead();
      const assignments = lead?.assignments || [];
      const sel = selectedAssignmentId ? assignments.find((a: { id: number }) => a.id === selectedAssignmentId) : assignments[0];
      loadCallLogs(sel?.user_id);
      loadStatistics();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log call');
    }
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested': return 'bg-green-100 text-green-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'callback_requested': return 'bg-blue-100 text-blue-800';
      case 'no_response': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-8">Loading...</div>
      </Layout>
    );
  }

  if (!lead) {
    return (
      <Layout>
        <div className="text-center py-8 text-red-600">Data not found</div>
      </Layout>
    );
  }

  const assignments = lead.assignments || [];
  const selectedAssignment = selectedAssignmentId
    ? assignments.find((a: { id: number }) => a.id === selectedAssignmentId)
    : assignments[0] || null;
  const selectedBrandId = selectedAssignment?.brand_id ?? null;
  const selectedBrandConverted =
    selectedBrandId !== null &&
    ((selectedAssignment as { brand_converted?: boolean })?.brand_converted === true ||
      (lead.converted_brand_ids || []).includes(selectedBrandId));
  const displayStatus = selectedAssignment?.status ?? lead.status;
  const displayLastCallDate = selectedAssignment?.last_call_date ?? lead.last_call_date;
  const displayNextCallDate = selectedAssignment?.next_call_date ?? lead.next_call_date;
  const displayTotalCalls = selectedAssignment != null ? selectedAssignment.total_calls : (lead.total_calls ?? 0);

  const openCallModal = () => {
    setCallFormData({
      entity_type: 'lead',
      entity_id: lead.id,
      lead_assignment_id: selectedAssignmentId ?? undefined,
      call_date: getTodayISO(),
      call_time: getNowTimeHHMM(),
      call_type: 'outbound'
    });
    setShowCallModal(true);
  };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => {
              const lr = (location.state as LeadNavState | null)?.listRestore;
              if (lr) {
                navigate('/leads', { state: { restore: lr } });
              } else {
                navigate('/leads');
              }
            }}
            className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
          >
            <Icon component={FiArrowLeft} className="w-4 h-4" />
            <span className="text-sm">Back to Data</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Data Info Card */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{lead.party_name}</h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Icon component={FiPhone} className="w-4 h-4" />
                  <span>{lead.contact_no}</span>
                </div>
                {lead.contact_no && (
                  <ClickToCallButton
                    entityType="lead"
                    entityId={lead.id}
                    entityName={lead.party_name}
                    phoneNumber={lead.contact_no}
                    className="ml-2"
                  />
                )}
                {lead.contact_person && (
                  <div className="flex items-center space-x-1">
                    <Icon component={FiUser} className="w-4 h-4" />
                    <span>{lead.contact_person}</span>
                  </div>
                )}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
                  >
                    <Icon component={FiMail} className="w-4 h-4" />
                    <span>{lead.email}</span>
                  </a>
                )}
                {lead.source && (
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                    {lead.source}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 sm:mt-0 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              {assignments.length > 1 && (
                <select
                  value={selectedAssignmentId ?? ''}
                  onChange={(e) => setSelectedAssignmentId(e.target.value ? parseInt(e.target.value) : null)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {assignments.map((a: { id: number; brand_name?: string; assigned_to_name?: string }) => (
                    <option key={a.id} value={a.id}>
                      {a.brand_name || 'No brand'} – {a.assigned_to_name || a.id}
                    </option>
                  ))}
                </select>
              )}
              <span className={`px-2 py-1 rounded text-xs font-semibold border ${
                displayStatus === 'INTERSTED' ? 'bg-green-100 text-green-800 border-green-300' :
                displayStatus === 'NOT INTERSTED' ? 'bg-red-100 text-red-800 border-red-300' :
                displayStatus === 'PROSPECT' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                displayStatus === 'EXISTING OTHERS' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                displayStatus === 'EXISTING INFRASOL' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                displayStatus === 'customer' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                'bg-gray-100 text-gray-800 border-gray-300'
              }`}>
                {displayStatus}
              </span>
              {(lead.conversion_count ?? 0) > 0 && (
                <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-300" title="At least one brand converted">
                  {(lead.conversion_count ?? 0)} conversion{(lead.conversion_count ?? 0) === 1 ? '' : 's'}
                </span>
              )}
              {selectedBrandConverted ? (
                <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-300">
                  This brand converted
                </span>
              ) : (
                <button
                  onClick={() => {
                    const preBrand = selectedBrandId ?? undefined;
                    setConvertFormData({
                      brand_id: preBrand,
                      status: 'inactive',
                      customer_code: '',
                      sap_id: '',
                      work_order_value: undefined,
                      notes: ''
                    });
                    setShowConvertModal(true);
                  }}
                  className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium flex items-center gap-1.5 font-bold"
                >
                  <Icon component={FiUserPlus} className="w-4 h-4" />
                  Convert to Customer
                </button>
              )}
            </div>
          </div>
          {lead.conversions && lead.conversions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-500">Customer records:</span>
              {lead.conversions.map((c) => (
                <Link
                  key={`${c.customer_id}-${c.brand_id ?? 'x'}`}
                  to={`/customers/${c.customer_id}`}
                  className="text-yellow-700 hover:text-yellow-900 font-medium underline"
                >
                  {c.customer_code || `ID ${c.customer_id}`}
                  {c.brand_id != null ? ` (brand #${c.brand_id})` : ''}
                </Link>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm">
            {lead.office_address && (
              <div>
                <span className="text-gray-500">Address:</span>
                <p className="text-gray-900">{lead.office_address}</p>
              </div>
            )}
            {lead.project_location && (
              <div>
                <span className="text-gray-500">Location:</span>
                <p className="text-gray-900">{lead.project_district && `${lead.project_district} - `}{lead.project_location}</p>
              </div>
            )}
            {lead.assigned_to_name && (
              <div>
                <span className="text-gray-500">Assigned To:</span>
                <p className="text-gray-900">{lead.assigned_to_name}</p>
              </div>
            )}
            {lead.product_req && (
              <div>
                <span className="text-gray-500">Product Req:</span>
                <p className="text-gray-900">{lead.product_req}</p>
              </div>
            )}
            {lead.qty_req && (
              <div>
                <span className="text-gray-500">Qty Req:</span>
                <p className="text-gray-900">{lead.qty_req}</p>
              </div>
            )}
            {lead.price && (
              <div>
                <span className="text-gray-500">Price:</span>
                <p className="text-gray-900">₹{lead.price.toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>

        {/* Call Statistics */}
        {statistics && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Statistics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{displayTotalCalls}</div>
                <div className="text-xs text-gray-600 mt-1">Total Calls (this assignment)</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{statistics.answered_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Answered</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{statistics.no_answer_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">No Answer</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{statistics.interested_calls || 0}</div>
                <div className="text-xs text-gray-600 mt-1">Interested</div>
              </div>
            </div>
            {displayNextCallDate && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon component={FiCalendar} className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="text-sm font-semibold text-orange-900">Next Call Scheduled</div>
                    <div className="text-sm text-orange-700">{formatDateTime(displayNextCallDate)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Call Log Button */}
        <div className="flex justify-end">
          <button
            onClick={openCallModal}
            className="flex items-center space-x-2 bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
          >
            <Icon component={FiPhone} className="w-4 h-4" />
            <span>Log New Call</span>
          </button>
        </div>

        {/* Call History Timeline */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Call History</h2>
          {callLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
          ) : (
            <div className="space-y-4">
              {callLogs.map((log) => (
                <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(log.call_date, log.call_time)}
                      </span>
                      {log.called_by_name && (
                        <span className="text-xs text-gray-500">
                          by {log.called_by_name}
                          {log.logged_by_name && log.logged_by_name !== log.called_by_name && (
                            <span className="text-gray-400"> (logged by {log.logged_by_name})</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                      {log.call_status && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getSettingColorClass(log.call_status_color_code)} border`}>
                          {log.call_status}
                        </span>
                      )}
                      {log.call_outcome && (
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}>
                          {log.call_outcome}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.notes && (
                    <div className="text-sm text-gray-700 mt-2 flex items-start space-x-2">
                      <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="flex-1">{log.notes}</p>
                    </div>
                  )}
                  {log.next_call_date && (
                    <div className="mt-2 text-xs text-indigo-600 flex items-center space-x-1">
                      <Icon component={FiCalendar} className="w-3 h-3" />
                      <span>Next call: {formatDateTime(log.next_call_date)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Price Update History */}
        {lead && (
          <PriceUpdateHistory
            entityType="lead"
            entityId={lead.id}
            entityName={lead.party_name}
          />
        )}

        {/* Call Log Modal */}
        {showCallModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Log New Call</h3>
                <button
                  type="button"
                  onClick={() => setShowCallModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              {lead && <PriorCallAlert entityType="lead" entityId={lead.id} />}
              <form onSubmit={handleCallSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                    <input
                      type="date"
                      required
                      value={callFormData.call_date}
                      onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                    <input
                      type="time"
                      value={callFormData.call_time}
                      onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status</label>
                  <CallStatusSelect
                    value={callFormData.call_status_id}
                    onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome</label>
                  <select
                    value={callFormData.call_outcome || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                    disabled={callOutcomeOptionsLoading}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                  >
                    <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                    {callOutcomeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={callFormData.notes || ''}
                    onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={4}
                    placeholder="Enter call notes..."
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                  <input
                    type="datetime-local"
                    value={callFormData.next_call_date ? (() => {
                      // Convert ISO string (UTC) to local datetime-local format (YYYY-MM-DDTHH:mm)
                      const date = new Date(callFormData.next_call_date);
                      // Get local time components
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      return `${year}-${month}-${day}T${hours}:${minutes}`;
                    })() : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        // datetime-local value is in local time, convert to ISO (UTC) for storage
                        // Create date from local datetime string (browser interprets as local time)
                        const localDateTime = e.target.value; // Format: YYYY-MM-DDTHH:mm (local time)
                        const date = new Date(localDateTime);
                        // Convert to ISO string (UTC) for backend storage
                        setCallFormData({ ...callFormData, next_call_date: date.toISOString() });
                      } else {
                        setCallFormData({ ...callFormData, next_call_date: undefined });
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
                {callFormData.next_call_date && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
                    <textarea
                      value={callFormData.next_call_notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, next_call_notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={2}
                      placeholder="Notes for next call..."
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Log Call
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Convert to Customer Modal */}
        {showConvertModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Convert Data to Customer
                </h3>
                <button
                  type="button"
                  onClick={() => setShowConvertModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                try {
                  const result = await leadService.convertToCustomer(parseInt(id || '0'), convertFormData);
                  setShowConvertModal(false);
                  setError('');
                  await loadLead();
                  alert(`Converted successfully. Customer ID: ${result.customer_id}. You can open the customer from the links below or convert another brand.`);
                } catch (err: any) {
                  setError(err.response?.data?.message || 'Failed to convert data to customer');
                }
              }} className="space-y-3 sm:space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Data:</strong> {lead?.party_name}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    This will create a new customer record and link it to this data.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Brand *</label>
                    <select
                      required
                      value={convertFormData.brand_id || ''}
                      onChange={(e) => setConvertFormData({ ...convertFormData, brand_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="">Select Brand</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status *</label>
                    <select
                      required
                      value={convertFormData.status}
                      onChange={(e) => setConvertFormData({ ...convertFormData, status: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="inactive">Inactive</option>
                      <option value="active">Active</option>
                      <option value="dormant">Dormant</option>
                      <option value="lost">Lost</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer Code</label>
                    <input
                      type="text"
                      value={convertFormData.customer_code}
                      onChange={(e) => setConvertFormData({ ...convertFormData, customer_code: e.target.value })}
                      placeholder="Auto-generated if empty"
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">SAP ID</label>
                    <input
                      type="text"
                      value={convertFormData.sap_id}
                      onChange={(e) => setConvertFormData({ ...convertFormData, sap_id: e.target.value })}
                      placeholder="Enter SAP ID if available"
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Work Order Value</label>
                    <input
                      type="number"
                      step="0.01"
                      value={convertFormData.work_order_value || ''}
                      onChange={(e) => setConvertFormData({ ...convertFormData, work_order_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Conversion Notes</label>
                  <textarea
                    value={convertFormData.notes}
                    onChange={(e) => setConvertFormData({ ...convertFormData, notes: e.target.value })}
                    className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    rows={3}
                    placeholder="Any additional notes about this conversion..."
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
                    {error}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowConvertModal(false);
                      setError('');
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    Convert to Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LeadDetail;

